import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';

import Button from '_components/atomic/Button';

const { colors, devices, fontSizes, spaces } = theme;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledBanner = styled(forwardRef(({ color, ...props }, ref) => (<div ref={ref} {...props} />)))`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;

  padding-top: ${spaces.large14X}px;
  padding-bottom: ${spaces.large14X}px;

  ${({ color }) =>
    color &&
    css`
      background-color: ${color};
    `}

  @media ${devices.tablet} {
    padding-top: ${spaces.large3X}px;
    padding-bottom: ${spaces.large3X}px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledBannerContainer = styled(forwardRef(({ reverse, ...props }, ref) => (<div ref={ref} {...props} />)))`
  display: flex;
  justify-content: space-between;
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  padding: ${spaces.regular}px;

  ${({ reverse }) =>
    reverse &&
    css`
      flex-direction: row-reverse;
    `}

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ desktop, mobile, ...props }, ref) => (<div ref={ref} {...props} />)))`
  display: flex;
  justify-content: center;
  align-items: center;

  min-height: 450px;
  width: 50%;

  img {
    max-height: 450px;
  }

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    width: 100%;
    min-height: 200px;
    margin-bottom: ${spaces.regular}px;

    img {
      max-height: 200px;
    }

    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledBannerBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 50%;

  @media ${devices.tablet} {
    align-items: center;
    width: 100%;
  }
`;

const StyledTitle = styled.div`
  margin-bottom: ${spaces.largeX + 2}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large11X}px;
  line-height: 130%;
  color: ${colors.gray[800]};

  @media ${devices.tablet} {
    margin-bottom: ${spaces.regular}px;
    font-size: ${fontSizes.large2X}px;
  }
`;

const StyledSubtitle = styled.div`
  margin-bottom: ${spaces.smallX}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    margin-bottom: 0;
    font-size: ${fontSizes.small}px;
  }
`;

const StyledDescription = styled.div`
  margin-bottom: ${spaces.largeX + 2}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  text-align: left;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    margin-bottom: ${spaces.regular}px;
    font-size: ${fontSizes.regular}px;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  width: 100%;

  @media ${devices.tablet} {
    flex-direction: column;
    align-items: center;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ServiceInfoSection = ({
  reverse,
  imgSrc,
  color,
  subtitle,
  title,
  description,
  onClick,
}) => {
  return (
    <StyledBanner color={color}>
      <StyledBannerContainer reverse={reverse}>
        <StyledBannerBody>
          <StyledSubtitle>{subtitle}</StyledSubtitle>
          <StyledTitle>{title}</StyledTitle>
          <StyledImageContainer mobile>
            <picture>
              <source srcSet={imgSrc} type="image/svg+xml" />
              <img src={imgSrc} alt="" />
            </picture>
          </StyledImageContainer>
          <StyledDescription>{description}</StyledDescription>
          <StyledButtonContainer>
            <Button outlined color={colors.indigo[600]} onClick={onClick}>
              Learn more
            </Button>
          </StyledButtonContainer>
        </StyledBannerBody>
        <StyledImageContainer desktop>
          <picture>
            <source srcSet={imgSrc} type="image/svg+xml" />
            <img src={imgSrc} alt="" />
          </picture>
        </StyledImageContainer>
      </StyledBannerContainer>
    </StyledBanner>
  );
};

ServiceInfoSection.propTypes = {
  reverse: PropTypes.bool,
  imgSrc: PropTypes.string,
  color: PropTypes.string,
  subtitle: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  onClick: PropTypes.func,
};

export default ServiceInfoSection;
